import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Paper,
  Autocomplete,
  Chip,
  Alert,
} from '@mui/material';
import { useSession } from '@platform/core/src/context/SessionContext';
import * as locationApi from '../../services/locationApi';
import * as workoutPlanApi from '../../services/workoutPlanApi';
import * as exerciseDefinitionApi from '../../services/exerciseDefinitionApi';
import * as locationFeatureApi from '../../services/locationFeatureApi';

const resourceConfig = {
  location: {
    fetcher: locationApi.getMyLocations,
    label: 'Location',
    pluralLabel: 'Locations',
    getOptionLabel: (item) => item.name,
    getOptionValue: (item) => item.id,
  },
  workout_plan: {
    fetcher: workoutPlanApi.getWorkoutPlans,
    label: 'Workout Plan',
    pluralLabel: 'Workout Plans',
    getOptionLabel: (item) => item.name,
    getOptionValue: (item) => item.id,
  },
  activity: {
    fetcher: () => exerciseDefinitionApi.searchExerciseDefinitions(),
    label: 'Activity',
    pluralLabel: 'Activities',
    getOptionLabel: (item) => item.name,
    getOptionValue: (item) => item.id,
  },
  equipment: {
    fetcher: locationFeatureApi.getLocationFeatures,
    label: 'Equipment',
    pluralLabel: 'Equipment',
    getOptionLabel: (item) => item.name,
    getOptionValue: (item) => item.id,
  },
};

const ResourceSelectionMessage = ({ requests, eventId }) => {
  const { sendMessage } = useSession();
  const [selections, setSelections] = useState({});
  const [resources, setResources] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fetchedResourceTypes = useRef(new Set());

  useEffect(() => {
    const fetchAllResources = async () => {
      const initialSelections = {};
      requests.forEach(req => {
        initialSelections[req.resource_type] = [];
      });
      setSelections(initialSelections);

      for (const request of requests) {
        const { resource_type } = request;
        const config = resourceConfig[resource_type];
        if (config && !fetchedResourceTypes.current.has(resource_type)) {
          fetchedResourceTypes.current.add(resource_type);
          setLoadingStates(prev => ({ ...prev, [resource_type]: true }));
          setErrorStates(prev => ({ ...prev, [resource_type]: null }));
          try {
            const data = await config.fetcher();
            setResources(prev => ({ ...prev, [resource_type]: data }));
          } catch (error) {
            console.error(`Failed to fetch ${resource_type}:`, error);
            setErrorStates(prev => ({ ...prev, [resource_type]: error.message || 'Failed to load data.' }));
          } finally {
            setLoadingStates(prev => ({ ...prev, [resource_type]: false }));
          }
        }
      }
    };

    fetchAllResources();
  }, [requests]);

  const handleSelectionChange = (resourceType, value) => {
    setSelections(prev => ({
      ...prev,
      [resourceType]: Array.isArray(value) ? value : (value ? [value] : []),
    }));
  };

  const { isSubmitDisabled, showDismissButton } = useMemo(() => {
    let allRequirementsMet = true;
    let allOptional = true;
    for (const req of requests) {
      const { resource_type, selection_config } = req;
      const { min, optional } = selection_config;
      const currentSelection = selections[resource_type] || [];
      if (!optional) {
        allOptional = false;
        if (currentSelection.length < min) {
          allRequirementsMet = false;
        }
      }
    }
    return { isSubmitDisabled: !allRequirementsMet, showDismissButton: allOptional };
  }, [selections, requests]);

  const handleSubmit = () => {
    const responseLines = ['[RESPONSE]'];
    responseLines.push(`event_id: ${eventId}`);
    for (const resourceType in selections) {
      const selectedIds = selections[resourceType];
      if (selectedIds && selectedIds.length > 0) {
        responseLines.push(`${resourceType}: ${selectedIds.join(', ')}`);
      }
    }
    responseLines.push('[/RESPONSE]');
    const messageContent = responseLines.join('\n');
    sendMessage(messageContent);
    setIsSubmitted(true);
  };

  const handleDismiss = () => {
    const messageContent = `[RESPONSE]\nevent_id: ${eventId}\n[/RESPONSE]`;
    sendMessage(messageContent);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    const submittedSelections = Object.entries(selections).filter(([, value]) => value.length > 0);
    return (
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.disabledBackground' }}>
        <Typography variant="subtitle1" gutterBottom>Selection Submitted</Typography>
        {submittedSelections.length > 0 ? (
          submittedSelections.map(([resourceType, selectedIds]) => {
            const config = resourceConfig[resourceType];
            const resourceList = resources[resourceType] || [];
            const selectedItems = resourceList.filter(item => selectedIds.includes(config.getOptionValue(item)));
            return (
              <Box key={resourceType} sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{config.pluralLabel}:</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {selectedItems.map(item => <Chip key={config.getOptionValue(item)} label={config.getOptionLabel(item)} size="small" />)}
                </Stack>
              </Box>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary">No items were selected.</Typography>
        )}
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Typography variant="h6">Please make your selections</Typography>
        {requests.map(request => {
          const { resource_type, selection_config } = request;
          const config = resourceConfig[resource_type];
          if (!config) {
            return <Alert severity="error" key={resource_type}>Unknown resource type: {resource_type}</Alert>;
          }

          const isLoading = loadingStates[resource_type];
          const error = errorStates[resource_type];
          const options = resources[resource_type] || [];
          const { min, max, optional } = selection_config;
          const isMultiSelect = max > 1;
          const label = `${optional ? '(Optional) ' : ''}Select ${isMultiSelect ? `up to ${max}` : 'a'} ${config.label}`;

          if (isLoading) {
            return <Box key={resource_type} sx={{ display: 'flex', alignItems: 'center' }}><CircularProgress size={20} sx={{ mr: 1 }} /><Typography>Loading {config.pluralLabel}...</Typography></Box>;
          }

          if (error) {
            return <Alert severity="error" key={resource_type}>{error}</Alert>;
          }

          return (
            <Box key={resource_type}>
              {isMultiSelect ? (
                <Autocomplete
                  multiple
                  options={options}
                  renderOption={(props, option) => (
                    <li {...props} key={config.getOptionValue(option)}>
                      {config.getOptionLabel(option)}
                    </li>
                  )}
                  getOptionLabel={config.getOptionLabel}
                  value={options.filter(opt => (selections[resource_type] || []).includes(config.getOptionValue(opt)))}
                  onChange={(event, newValue) => {
                    const newIds = newValue.map(item => config.getOptionValue(item));
                    handleSelectionChange(resource_type, newIds);
                  }}
                  getOptionDisabled={(option) =>
                    (selections[resource_type]?.length || 0) >= max &&
                    !selections[resource_type]?.includes(config.getOptionValue(option))
                  }
                  renderInput={(params) => <TextField {...params} label={label} />}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={config.getOptionLabel(option)} {...getTagProps({ index })} />
                    ))
                  }
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>{label}</InputLabel>
                  <Select
                    value={(selections[resource_type] && selections[resource_type][0]) || ''}
                    label={label}
                    onChange={(e) => handleSelectionChange(resource_type, e.target.value)}
                  >
                    {optional && <MenuItem value=""><em>None</em></MenuItem>}
                    {options.map(option => (
                      <MenuItem key={config.getOptionValue(option)} value={config.getOptionValue(option)}>
                        {config.getOptionLabel(option)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {`Required: ${min} - ${max}`}
              </Typography>
            </Box>
          );
        })}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {showDismissButton && <Button onClick={handleDismiss}>Dismiss</Button>}
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled}>Submit</Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

ResourceSelectionMessage.propTypes = {
  requests: PropTypes.arrayOf(PropTypes.shape({
    resource_type: PropTypes.string.isRequired,
    selection_config: PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
      optional: PropTypes.bool.isRequired,
    }).isRequired,
  })).isRequired,
  eventId: PropTypes.string.isRequired,
};

export default ResourceSelectionMessage;